import {
  ApiError, Button, Card, ErrorState, Field, Input, Notice, Row, Select, Stack, TenantIdentityFields, identityBody,
  identityValuesOf,
  useApi, useI18n,
  type TenantProfileResponse, type TenantType,
} from '@daghfal/core';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

const TYPES: TenantType[] = ['Nursery', 'Kindergarten', 'Both'];

/** What the platform alone may change about a tenant: where it is reached, and how its times and money read. */
interface Placement {
  slug: string;
  type: TenantType;
  country: string;
  timeZone: string;
  currency: string;
}


function placementOf(tenant: TenantProfileResponse): Placement {
  return {
    slug: tenant.slug,
    type: tenant.type,
    country: tenant.country ?? '',
    timeZone: tenant.timeZone,
    currency: tenant.currency,
  };
}

/**
 * Correcting a tenant (C7, FR-PLT-05), behind `platform.tenants.update`.
 *
 * A new entry path stops every link already sent, and a new timezone moves every stored time, so either asks to be
 * confirmed here before the save; the API refuses it unconfirmed too. The currency is refused once the nursery has
 * invoiced, in the API's own words.
 */
export function TenantEditCard({ tenant, onChanged }: {
  tenant: TenantProfileResponse;
  onChanged: (updated: TenantProfileResponse) => void;
}) {
  const api = useApi();
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [identity, setIdentity] = useState(() => identityValuesOf(tenant));
  const [placement, setPlacement] = useState(() => placementOf(tenant));
  const [confirmed, setConfirmed] = useState(false);

  const slugChanges = placement.slug !== tenant.slug;
  const zoneChanges = placement.timeZone.trim() !== tenant.timeZone;
  const needsConfirming = slugChanges || zoneChanges;

  const save = useMutation({
    mutationFn: () => api.platform.updateTenant(tenant.id, {
      ...identityBody(identity),
      slug: placement.slug,
      type: placement.type,
      country: placement.country.trim() || null,
      timeZone: placement.timeZone.trim(),
      currency: placement.currency.trim(),
      confirmLinksAndTimes: needsConfirming && confirmed,
    }),
    onSuccess: (updated) => { setEditing(false); onChanged(updated); },
  });

  const open = () => {
    setIdentity(identityValuesOf(tenant));
    setPlacement(placementOf(tenant));
    setConfirmed(false);
    save.reset();
    setEditing(true);
  };

  const set = <K extends keyof Placement>(key: K, value: Placement[K]) =>
    setPlacement((current) => ({ ...current, [key]: value }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  if (!editing) {
    return (
      <Card title={t('tenant.edit')} action={<Button variant="secondary" onClick={open}>{t('action.edit')}</Button>}>
        {save.isSuccess && <Notice tone="success">{t('tenant.edit.saved')}</Notice>}
      </Card>
    );
  }

  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : {};

  return (
    <Card title={t('tenant.edit')}>
      <form noValidate onSubmit={submit}>
        <Stack>
          <TenantIdentityFields value={identity} onChange={setIdentity} errors={fieldErrors} />

          <Field
            label={t('tenants.new.slug')}
            hint={t('tenants.new.slug.hint', { slug: placement.slug || '…' })}
            error={fieldErrors.slug}
            required
          >
            {(props) => (
              <Input
                {...props}
                dir="ltr"
                value={placement.slug}
                // Normalised as it is typed, as on creation, so the field shows the link it will make.
                onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              />
            )}
          </Field>

          <Field label={t('tenants.new.type')} required>
            {(props) => (
              <Select {...props} value={placement.type} onChange={(e) => set('type', e.target.value as TenantType)}>
                {TYPES.map((type) => <option key={type} value={type}>{t(`tenants.new.type.${type}`)}</option>)}
              </Select>
            )}
          </Field>

          <Row>
            <Field label={t('tenants.new.country')}>
              {(props) => (
                <Input {...props} dir="ltr" value={placement.country} onChange={(e) => set('country', e.target.value.toUpperCase())} />
              )}
            </Field>
            <Field label={t('tenants.new.timeZone')} required>
              {(props) => (
                <Input {...props} dir="ltr" value={placement.timeZone} onChange={(e) => set('timeZone', e.target.value)} />
              )}
            </Field>
            <Field label={t('tenants.new.currency')} required>
              {(props) => (
                <Input {...props} dir="ltr" value={placement.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} />
              )}
            </Field>
          </Row>

          {needsConfirming && (
            <Notice tone="warning">
              <Stack tight>
                {slugChanges && <span>{t('tenant.edit.slugChanges', { slug: tenant.slug })}</span>}
                {zoneChanges && (
                  <span>{t('tenant.edit.zoneChanges', { zone: placement.timeZone.trim(), current: tenant.timeZone })}</span>
                )}
                <label className="dg-checkbox">
                  <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                  <span>{t('tenant.edit.confirm')}</span>
                </label>
              </Stack>
            </Notice>
          )}

          {save.isError && <ErrorState error={save.error} />}
          <Row>
            <Button
              type="submit"
              disabled={needsConfirming && !confirmed}
              busy={save.isPending}
              busyLabel={t('action.saving')}
            >
              {t('action.save')}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}
